import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestoreIcon from "@mui/icons-material/Restore";
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemSwitch } from "../../components/UIToolkit/Clickable/SystemSwitch";
import { SystemValidationTextField } from "../../components/UIToolkit/Inputs/SystemValidationTextField";
import { MultiSizePopup } from "../../components/UIToolkit/Popups/MultiSizePopup/MultiSizePopup";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import type { TranslationKeys } from "../../i18n/types";
import { type DisciplinaryRule, DisciplinaryViolationType } from "../../utils/models";
import { useGetDisciplinaryRules, useSaveDisciplinaryRules } from "./useDisciplinaryReport";

type ViolationTypeConfig = {
	type: DisciplinaryViolationType;
	label: TranslationKeys;
	thresholdLabel?: TranslationKeys;
	maxThresholdLabel?: TranslationKeys;
	thresholdUnit: string;
};

const VIOLATION_TYPE_CONFIGS = [
	{
		type: DisciplinaryViolationType.NCNS,
		label: "disciplinary.violation.ncns",
		thresholdUnit: "",
	},
	{
		type: DisciplinaryViolationType.CallIn,
		label: "disciplinary.violation.call_in",
		thresholdLabel: "disciplinary.threshold.hours_before_shift",
		maxThresholdLabel: "disciplinary.threshold.max_hours_before_shift",
		thresholdUnit: "h",
	},
	{
		type: DisciplinaryViolationType.LateArrival,
		label: "disciplinary.violation.late_arrival",
		thresholdLabel: "disciplinary.threshold.min_minutes_late",
		maxThresholdLabel: "disciplinary.threshold.max_minutes_late",
		thresholdUnit: "min",
	},
	{
		type: DisciplinaryViolationType.HomeEarly,
		label: "disciplinary.violation.home_early",
		thresholdLabel: "disciplinary.threshold.min_minutes_early",
		maxThresholdLabel: "disciplinary.threshold.max_minutes_early",
		thresholdUnit: "min",
	},
	{
		type: DisciplinaryViolationType.ShiftDelta,
		label: "disciplinary.violation.shift_delta",
		thresholdLabel: "disciplinary.threshold.min_minutes_delta",
		maxThresholdLabel: "disciplinary.threshold.max_minutes_delta",
		thresholdUnit: "min",
	},
	{
		type: DisciplinaryViolationType.SkippedLunch,
		label: "disciplinary.violation.skipped_lunch",
		thresholdUnit: "",
	},
	{
		type: DisciplinaryViolationType.MissingPunch,
		label: "disciplinary.violation.missing_punch",
		thresholdUnit: "",
	},
] as const satisfies readonly ViolationTypeConfig[];

const DEFAULT_RULE_POINTS_BY_TYPE: Partial<Record<DisciplinaryViolationType, number>> = {
	[DisciplinaryViolationType.SkippedLunch]: 1,
	[DisciplinaryViolationType.MissingPunch]: 0.5,
};

type Props = {
	open: boolean;
	onClose: () => void;
};

export const DisciplinarySettingsDialog: FC<Props> = ({ open, onClose }) => {
	const { t } = useTranslation();
	const { data: rulesData, isLoading } = useGetDisciplinaryRules();
	const { mutateAsync: saveRules, isPending: isSaving } = useSaveDisciplinaryRules();
	const [localRules, setLocalRules] = useState<DisciplinaryRule[]>([]);

	useEffect(() => {
		if (open && rulesData?.rules) {
			setLocalRules(rulesData.rules);
		}
	}, [open, rulesData]);

	const rulesByType = useMemo(() => {
		const grouped: Record<string, DisciplinaryRule[]> = {};
		for (const rule of localRules) {
			if (!grouped[rule.violationType]) grouped[rule.violationType] = [];
			grouped[rule.violationType].push(rule);
		}
		for (const key in grouped) {
			grouped[key].sort((a, b) => a.sortOrder - b.sortOrder);
		}
		return grouped;
	}, [localRules]);

	const updateRule = useCallback((type: DisciplinaryViolationType, sortOrder: number, field: keyof DisciplinaryRule, value: number | null | boolean) => {
		setLocalRules((prev) => prev.map((r) => (r.violationType === type && r.sortOrder === sortOrder ? { ...r, [field]: value } : r)));
	}, []);

	const addRule = useCallback((type: DisciplinaryViolationType) => {
		setLocalRules((prev) => {
			const existing = prev.filter((r) => r.violationType === type);
			const maxOrder = existing.length > 0 ? Math.max(...existing.map((r) => r.sortOrder)) : -1;
			const defaultPoints = DEFAULT_RULE_POINTS_BY_TYPE[type] ?? 0;
			return [
				...prev,
				{
					violationType: type,
					thresholdValue: 0,
					maxThresholdValue: null,
					weekdayPoints: defaultPoints,
					weekendPoints: defaultPoints,
					sortOrder: maxOrder + 1,
					countOnlyWhenActualLonger: false,
				},
			];
		});
	}, []);

	const removeRule = useCallback((type: DisciplinaryViolationType, sortOrder: number) => {
		setLocalRules((prev) => prev.filter((r) => !(r.violationType === type && r.sortOrder === sortOrder)));
	}, []);

	const handleResetDefaults = useCallback(() => {
		// Must stay in sync with the backend DEFAULT_DISCIPLINARY_RULES (backend/services/reports.py),
		// the same set new departments are seeded with. Call-in is tiered by hours of notice.
		setLocalRules([
			{
				violationType: DisciplinaryViolationType.NCNS,
				thresholdValue: 0,
				maxThresholdValue: null,
				weekdayPoints: 1,
				weekendPoints: 1,
				sortOrder: 0,
				countOnlyWhenActualLonger: false,
			},
			{
				violationType: DisciplinaryViolationType.CallIn,
				thresholdValue: 1.5,
				maxThresholdValue: 3,
				weekdayPoints: 1,
				weekendPoints: 1,
				sortOrder: 0,
				countOnlyWhenActualLonger: false,
			},
			{
				violationType: DisciplinaryViolationType.CallIn,
				thresholdValue: 0,
				maxThresholdValue: 1.5,
				weekdayPoints: 3,
				weekendPoints: 3,
				sortOrder: 1,
				countOnlyWhenActualLonger: false,
			},
			{
				violationType: DisciplinaryViolationType.LateArrival,
				thresholdValue: 10,
				maxThresholdValue: null,
				weekdayPoints: 1,
				weekendPoints: 1,
				sortOrder: 0,
				countOnlyWhenActualLonger: false,
			},
			{
				violationType: DisciplinaryViolationType.HomeEarly,
				thresholdValue: 30,
				maxThresholdValue: null,
				weekdayPoints: 0.5,
				weekendPoints: 0.5,
				sortOrder: 0,
				countOnlyWhenActualLonger: false,
			},
			{
				violationType: DisciplinaryViolationType.ShiftDelta,
				thresholdValue: 60,
				maxThresholdValue: null,
				weekdayPoints: 1,
				weekendPoints: 1,
				sortOrder: 0,
				countOnlyWhenActualLonger: false,
			},
			{
				violationType: DisciplinaryViolationType.MissingPunch,
				thresholdValue: 0,
				maxThresholdValue: null,
				weekdayPoints: 0.5,
				weekendPoints: 0.5,
				sortOrder: 0,
				countOnlyWhenActualLonger: false,
			},
		]);
	}, []);

	const handleSave = async () => {
		await saveRules({ rules: localRules }, { onSuccess: onClose });
	};

	return (
		<MultiSizePopup
			open={open}
			onClose={onClose}
			closeOnEscape
			title={t("disciplinary.settings.configure_points")}
			maxContentWidth={"90%"}
			disableMinimize
			headerActions={
				<Tooltip title={t("disciplinary.settings.reset_defaults")}>
					<IconButton onClick={handleResetDefaults} size="small">
						<RestoreIcon fontSize="inherit" color="inherit" />
					</IconButton>
				</Tooltip>
			}
			bodyRenderer={() => (
				<Box sx={{ display: "flex", flexDirection: "column" }}>
					<Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
						{isLoading ? (
							<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
								<CircularProgress />
							</Box>
						) : (
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
								<Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
									{t("disciplinary.settings.description")}
								</Typography>
								{VIOLATION_TYPE_CONFIGS.map((config) => (
									<ViolationTypeAccordion
										key={config.type}
										config={config}
										rules={rulesByType[config.type] ?? []}
										onUpdateRule={updateRule}
										onAddRule={addRule}
										onRemoveRule={removeRule}
									/>
								))}
							</Box>
						)}
					</Box>
					<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
						<SystemButton onClick={onClose} buttonVariant="borderless" size="small">
							{t("cancel")}
						</SystemButton>
						<SystemButton onClick={handleSave} buttonVariant="primary" size="small" disabled={isLoading} loading={isSaving} sx={{ minWidth: 100 }}>
							{t("save")}
						</SystemButton>
					</Box>
				</Box>
			)}
		/>
	);
};

type ViolationTypeAccordionProps = {
	config: ViolationTypeConfig;
	rules: DisciplinaryRule[];
	onUpdateRule: (type: DisciplinaryViolationType, sortOrder: number, field: keyof DisciplinaryRule, value: number | null | boolean) => void;
	onAddRule: (type: DisciplinaryViolationType) => void;
	onRemoveRule: (type: DisciplinaryViolationType, sortOrder: number) => void;
};
const ViolationTypeAccordion: FC<ViolationTypeAccordionProps> = ({ config, rules, onUpdateRule, onAddRule, onRemoveRule }) => {
	const { t } = useTranslation();

	return (
		<Accordion defaultExpanded={rules.length > 0} sx={{ "&:before": { display: "none" } }}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "#F5F7FA" }}>
				<Typography sx={{ fontWeight: 600, color: "primary.dark" }}>{t(config.label)}</Typography>
				<Typography sx={{ ml: 1, color: "#999", fontSize: 13 }}>({rules.length})</Typography>
			</AccordionSummary>
			<AccordionDetails sx={{ pt: 2 }}>
				{rules.map((rule) => (
					<Box key={rule.sortOrder} sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1.5 }}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
							{!!config.thresholdLabel && (
								<>
									<SystemValidationTextField
										label={t(config.thresholdLabel)}
										inputType="number"
										value={String(rule.thresholdValue)}
										onChange={(e) => onUpdateRule(config.type, rule.sortOrder, "thresholdValue", Number(e.target.value))}
										reserveHeightForHelperText={false}
										formControlSx={{ flex: 1 }}
									/>
									<Typography sx={{ color: "#999", fontSize: 13 }}>{t("disciplinary.threshold.range_separator")}</Typography>
									<SystemValidationTextField
										label={config.maxThresholdLabel ? t(config.maxThresholdLabel) : ""}
										inputType="number"
										value={rule.maxThresholdValue == null ? "" : String(rule.maxThresholdValue)}
										onChange={(e) => {
											const next = e.target.value.trim();
											onUpdateRule(config.type, rule.sortOrder, "maxThresholdValue", next === "" ? null : Number(next));
										}}
										reserveHeightForHelperText={false}
										formControlSx={{ flex: 1 }}
									/>
								</>
							)}
							<SystemValidationTextField
								label={t("disciplinary.settings.weekday_pts")}
								inputType="number"
								value={String(rule.weekdayPoints)}
								onChange={(e) => onUpdateRule(config.type, rule.sortOrder, "weekdayPoints", Number(e.target.value))}
								reserveHeightForHelperText={false}
								formControlSx={{ flex: 1 }}
							/>
							<SystemValidationTextField
								label={t("disciplinary.settings.weekend_pts")}
								inputType="number"
								value={String(rule.weekendPoints)}
								onChange={(e) => onUpdateRule(config.type, rule.sortOrder, "weekendPoints", Number(e.target.value))}
								reserveHeightForHelperText={false}
								formControlSx={{ flex: 1 }}
							/>
							<Tooltip title={t("delete")}>
								<IconButton size="small" onClick={() => onRemoveRule(config.type, rule.sortOrder)} sx={{ color: "#D32F2F" }}>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Box>
						{config.type === DisciplinaryViolationType.ShiftDelta && (
							<SystemSwitch
								checked={!!rule.countOnlyWhenActualLonger}
								onChange={(e) => onUpdateRule(config.type, rule.sortOrder, "countOnlyWhenActualLonger", e.target.checked)}
								label={t("disciplinary.settings.count_only_when_actual_longer")}
							/>
						)}
					</Box>
				))}
				<SystemButton size="small" buttonVariant="borderless" onClick={() => onAddRule(config.type)} leadingIcon={<AddIcon />}>
					{t("disciplinary.settings.add_rule")}
				</SystemButton>
			</AccordionDetails>
		</Accordion>
	);
};
